import React, { useState } from "react";
import { Button, Box, Typography, Snackbar, Alert, CircularProgress } from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import api, { ensureCsrfToken } from "../utils/api";

const StepReview = ({ data, onBack, onNext }) => {
  const [celebrate, setCelebrate] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });
  const [finalStep, setFinalStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Adjust if your route differs
  const DASHBOARD_URL = "/user/dashboard";
  const REDIRECT_DELAY = 1200; // ms

  const handleComplete = async () => {
    try {
      setIsSubmitting(true);

      const formDataPayload = new FormData();

      // Documents
      if (Array.isArray(data.documents)) {
        data.documents.forEach((file, i) => {
          if (file instanceof File) {
            formDataPayload.append(`documents[${i}]`, file);
          }
        });
      }

      // Other fields (kept as-is)
      Object.entries(data).forEach(([key, value]) => {
        if (key === "documents") return;
        formDataPayload.append(key, value ?? "");
      });

      await ensureCsrfToken();

      const response = await api.post("/api/kyc/submit", formDataPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Success UX
      setCelebrate(true);
      setFinalStep(true);
      setSnackbar({
        open: true,
        message: response?.data?.message || "KYC submitted successfully.",
        type: "success",
      });

      // Preserve existing flow
      if (typeof onNext === "function") onNext();

      // Redirect after a short pause so the snackbar/confetti is visible
      setTimeout(() => {
        window.location.href = DASHBOARD_URL;
      }, REDIRECT_DELAY);
    } catch (err) {
      console.error("Error uploading KYC:", err);
      setSnackbar({ open: true, message: "Failed to submit KYC", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleGoDashboard = () => {
    window.location.href = DASHBOARD_URL;
  };

  const handleLogout = () => {
    window.location.href = "/logout";
  };

  return (
    <Box textAlign="center">
      {/* Confetti Celebration */}
      {celebrate && <Confetti recycle={false} numberOfPieces={400} />}

      {!finalStep ? (
        <>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
            🎉 Final Review
          </Typography>

          {/* Review Card */}
          <motion.div
            className="bg-black/40 border border-cyan-400 rounded-2xl p-4 mt-4 shadow-[0_0_15px_rgba(0,255,255,0.7)]"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ReviewRow label="Email" value={data.email} />
            <ReviewRow label="Full Name" value={`${data.first_name || ""} ${data.last_name || ""}`} />
            <ReviewRow label="Date of Birth" value={data.dob} />
            <ReviewRow label="National ID" value={data.national_id} />
            <ReviewRow label="Passport Number" value={data.passport_number} />
            <ReviewRow label="Address" value={data.address} />
            <ReviewRow label="City" value={data.city} />
            <ReviewRow label="Country" value={data.country} />
            <ReviewRow label="Postal Code" value={data.postal_code} />
          </motion.div>

          {/* Navigation */}
          <Box mt={4} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Button variant="outlined" onClick={onBack} disabled={isSubmitting}>
              Back
            </Button>

            <Button
              variant="contained"
              onClick={handleComplete}
              disabled={isSubmitting}
              sx={{
                background: "linear-gradient(90deg,#06b6d4,#3b82f6)",
                boxShadow: "0 0 12px #06b6d4",
                fontWeight: "bold",
                minWidth: 180,
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Submitting…
                </>
              ) : (
                <>
                  <SendRoundedIcon sx={{ mr: 1 }} />
                  Submit
                </>
              )}
            </Button>
          </Box>
        </>
      ) : (
        <Box
          mt={6}
          sx={{
            background: "rgba(0,0,0,0.7)",
            borderRadius: 3,
            p: 4,
            boxShadow: "0 0 15px rgba(0,255,255,0.4)",
          }}
        >
          <Typography variant="h6" gutterBottom>
            🎉 Your KYC has been submitted!
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You will be notified once your verification is approved.
          </Typography>

          <Box mt={3} sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button variant="outlined" onClick={handleGoDashboard}>
              Go to Dashboard
            </Button>
            <Button variant="contained" color="error" onClick={handleLogout} sx={{ fontWeight: "bold" }}>
              Logout
            </Button>
          </Box>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.type} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ReviewRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      py: 1,
      textAlign: "left",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="500">
      {value || "—"}
    </Typography>
  </Box>
);

export default StepReview;